import { useState } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { Activity, Compass, GitBranch, Layers, Network, ShieldCheck, Timer, UserRound } from 'lucide-react'
import Panel, { PanelError, PanelSkeleton } from '@/components/dashboards/Panel'
import { BasisNote } from '@/components/dashboards/KnownFigures'
import { useDrillDown } from '@/components/dashboards/useDrillDown'
import FlowChart from '@/components/analytics/explorer/FlowChart'
import FunnelStrip from '@/components/analytics/explorer/FunnelStrip'
import { usePersonProfile } from '@/components/analytics/explorer/analyticsQueries'
import {
  INTERNAL_ROLES,
  ROLE_DIMENSION,
  ROLE_LABEL,
  drillForIds,
  encodeFilters,
  profileFlow,
  type FlowDateField,
  type PeopleRole,
  type PersonAging,
  type PersonProfile,
} from '@/services/dashboardAnalytics'
import ActivityTimeline from './ActivityTimeline'
import MixBars from './MixBars'
import PersonDeals from './PersonDeals'
import PersonHeadline from './PersonHeadline'
import RelationshipSankey from './RelationshipSankey'
import RelationshipTable from './RelationshipTable'

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function explorerHref(role: PeopleRole, name: string): string | null {
  const dimension = ROLE_DIMENSION[role]
  if (!dimension) return null
  const cohort = encodeFilters({ [dimension]: [name] })
  return `/dashboards/ceo?view=explorer&xdim=stage&xby=outcome&cohort=${encodeURIComponent(cohort)}`
}

function AgingStrip({ aging, policy, name }: { aging: PersonAging; policy: PersonProfile['policy']; name: string }) {
  const { open } = useDrillDown()
  const cells: Array<{ key: string; label: string; count: number | undefined; ids: string[] | undefined; tone: 'neutral' | 'warning' | 'negative'; hint: string }> = [
    { key: 'triage', label: 'In triage', count: aging.triageCount, ids: aging.triageIds, tone: 'neutral', hint: 'Deals in Triage now' },
    {
      key: 'triageSla',
      label: `Triage ${policy?.responseSlaHours ?? 24}h+`,
      count: aging.triageOverResponseSlaCount,
      ids: aging.triageOverResponseSlaIds,
      tone: 'warning',
      hint: 'Still in Triage a full 24-hour day after entering it — no outreach is recorded, so a stage move is the response proxy (T-72)',
    },
    {
      key: 'triageAged',
      label: `Triage ${policy?.triageAgedDays ?? 14}d+`,
      count: aging.triageAgedCount,
      ids: aging.triageAgedIds,
      tone: 'warning',
      hint: 'Neither quoted nor killed (T-73)',
    },
    { key: 'quotes', label: 'Quotes out', count: aging.quotesOutCount, ids: aging.quotesOutIds, tone: 'neutral', hint: 'Deals at Post-screen Quote now' },
    {
      key: 'quotesAged',
      label: `Quotes >${policy?.quoteAgedDays ?? 14}d`,
      count: aging.quotesOutAgedCount,
      ids: aging.quotesOutAgedIds,
      tone: 'warning',
      hint: 'Quote out with no term sheet and no kill (T-74)',
    },
    {
      key: 'quoteSla',
      label: `Quote SLA ${policy?.quoteSlaDays ?? 3}d`,
      count: aging.quoteSlaBreachCount,
      ids: aging.quoteSlaBreachIds,
      tone: 'negative',
      hint: 'Not quoted within the SLA after receipt (T-72; 24-hour days, weekends count)',
    },
  ]
  return (
    <div className="grid grid-cols-3 gap-1.5 lg:grid-cols-6">
      {cells.map((cell) => {
        const count = cell.count ?? null
        const drill = count ? drillForIds(cell.ids, `${name} — ${cell.label}`) : null
        return (
          <button
            key={cell.key}
            type="button"
            onClick={() => open(drill)}
            disabled={!drill}
            title={cell.hint}
            className={clsx(
              'rounded-md border px-2 py-1.5 text-left transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50 disabled:cursor-default',
              count && cell.tone === 'negative'
                ? 'border-red-500/25 bg-red-500/[0.05] hover:border-red-400/50'
                : count && cell.tone === 'warning'
                  ? 'border-amber-400/25 bg-amber-400/[0.04] hover:border-amber-300/50'
                  : 'border-amber-400/[0.08] bg-[#0a0a0a] hover:border-amber-400/25',
            )}
          >
            <div className="truncate font-mono text-[8.5px] uppercase tracking-[0.14em] text-stone-500">{cell.label}</div>
            <div
              className={clsx(
                'font-mono text-[15px] font-semibold tabular-nums',
                count == null ? 'text-stone-600' : count && cell.tone === 'negative' ? 'text-red-300' : count && cell.tone === 'warning' ? 'text-amber-200' : 'text-stone-100',
              )}
            >
              {count == null ? '--' : count}
            </div>
          </button>
        )
      })}
    </div>
  )
}

interface PersonProfileViewProps {
  role: PeopleRole
  name: string
}

/**
 * One person's world (T-81: "pick a team member, that team member's world
 * appears"). Headline with peer rank, aging, flow, funnel, where their deals
 * come from, what they are made of, what happened lately, and every deal —
 * Responsible for and Supporting, split by stage. Every number drills.
 */
export default function PersonProfileView({ role, name }: PersonProfileViewProps) {
  const query = usePersonProfile(role, name)
  const [flowField, setFlowField] = useState<FlowDateField>('event')
  const profile = query.data

  if (query.error) {
    const status = (query.error as { response?: { status?: number } })?.response?.status
    return (
      <div className="p-2">
        {status === 404 ? (
          <div className="rounded-md border border-amber-400/15 bg-[#0a0a0a] px-4 py-6 text-center">
            <p className="text-[12px] text-stone-200">{name} is not named as {ROLE_LABEL[role].toLowerCase()} on any live deal or on the team roster.</p>
            <p className="mt-1 text-[11px] text-stone-500">Pick another role, or another name from the list.</p>
          </div>
        ) : (
          <PanelError error={query.error} onRetry={() => void query.refetch()} />
        )}
      </div>
    )
  }
  if (query.isLoading || !profile) {
    return (
      <div className="space-y-2 p-1">
        <div className="h-[88px] rounded-lg border border-amber-400/[0.08] bg-[#0a0a0a] p-3">
          <PanelSkeleton rows={3} />
        </div>
        <div className="h-[260px] rounded-lg border border-amber-400/[0.08] bg-[#0a0a0a] p-3">
          <PanelSkeleton rows={8} />
        </div>
      </div>
    )
  }

  const internal = INTERNAL_ROLES.has(role)
  const explorer = explorerHref(role, profile.name)
  const flow = profileFlow(profile, flowField)
  const holdsLabel = role === 'responsibleParty' || role === 'team' ? 'Responsible for' : `As ${ROLE_LABEL[role].toLowerCase()}`

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {/* Identity band */}
      <section className="relative overflow-hidden rounded-lg border border-amber-400/[0.12] bg-[radial-gradient(120%_140%_at_0%_0%,rgba(196,154,67,0.12),transparent_55%),linear-gradient(180deg,#121110,#0a0a0a)] px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-1 items-center gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-amber-300/40 bg-black/40 font-display text-[20px] text-amber-100">
            {initials(profile.name) || <UserRound size={18} />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate font-display text-[26px] font-medium leading-none text-stone-50">{profile.name}</h2>
              {profile.onRoster && (
                <span className="flex items-center gap-1 rounded border border-emerald-400/25 bg-emerald-400/[0.06] px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-widest text-emerald-300">
                  <ShieldCheck size={9} aria-hidden="true" /> Team roster
                </span>
              )}
            </div>
            <p className="mt-1 font-mono text-[9.5px] uppercase tracking-[0.2em] text-amber-200/70">
              {profile.roleLabel ?? ROLE_LABEL[role]}
            </p>
            {profile.basis && <p className="mt-1 max-w-[880px] text-[10.5px] leading-snug text-stone-500">{profile.basis}</p>}
          </div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            {internal && profile.responsibleFor && (
              <span className="rounded border border-amber-400/15 px-2 py-1 font-mono text-[10px] tabular-nums text-stone-300">
                Responsible for {profile.responsibleFor.count} · Supporting {profile.supporting?.count ?? 0}
              </span>
            )}
            {explorer && (
              <Link
                to={explorer}
                className="flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10"
              >
                <Compass size={10} aria-hidden="true" /> Open in Pipeline Explorer
              </Link>
            )}
          </div>
        </div>
      </section>

      <PersonHeadline headline={profile.headline} deals={profile.deals} name={profile.name} />

      {internal && profile.aging && (
        <section className="rounded-lg border border-amber-400/[0.08] bg-[#0a0a0a] p-2">
          <div className="mb-1.5 flex items-center gap-1.5">
            <Timer size={11} className="text-amber-300/80" aria-hidden="true" />
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-400">Response &amp; aging</span>
            <span className="text-[9.5px] leading-snug text-stone-600">
              <BasisNote full={profile.aging.basis}>
                Time in stage on the deals they are Responsible Party for; no hand-entered last contact.
              </BasisNote>
            </span>
          </div>
          <AgingStrip aging={profile.aging} policy={profile.policy} name={profile.name} />
        </section>
      )}

      <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
        <Panel
          title="Monthly flow"
          kicker={
            <select
              aria-label="Month of"
              value={flowField}
              onChange={(event) => setFlowField(event.target.value as FlowDateField)}
              className="rounded border border-amber-400/15 bg-[#090909] px-1 py-0.5 text-[10px] text-stone-300 focus:outline-none"
            >
              <option value="event">Each event date</option>
              <option value="received">By received date</option>
              <option value="funded">By funded date</option>
            </select>
          }
          className="h-[300px]"
          bodyClassName="flex min-h-0 flex-col !pb-1"
        >
          <FlowChart data={flow} series={['received', 'signed', 'funded']} scopeLabel={profile.name} />
        </Panel>
        <Panel
          title="Personal funnel"
          kicker="Stage now · conversion · dwell"
          className="h-[300px]"
          bodyClassName="!py-2"
          scroll
          footer={
            <BasisNote full={profile.funnel?.basis}>
              Deals in each stage now; % = share that reached the next stage; dwell in 24-hour days.
            </BasisNote>
          }
        >
          <FunnelStrip data={profile.funnel} orientation="column" scopeLabel={profile.name} />
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
        <Panel title="Where the deals come from" kicker={<span className="flex items-center gap-1"><Network size={10} aria-hidden="true" /> Source → person → outcome</span>} className="h-[340px]" bodyClassName="flex min-h-0 flex-col !pb-1">
          <RelationshipSankey sankey={profile.sankey} personName={profile.name} />
        </Panel>
        <Panel title="Relationships that feed the book" kicker="By deal flow" className="h-[340px]" bodyClassName="!p-0 flex min-h-0 flex-col">
          <RelationshipTable
            relationships={profile.relationships}
            deals={profile.deals}
            personName={profile.name}
            quietDays={profile.policy?.relationshipQuietDays ?? null}
          />
        </Panel>
      </div>

      <Panel title="Book mix" kicker={<span className="flex items-center gap-1"><Layers size={10} aria-hidden="true" /> Deals · total commitment</span>} bodyClassName="!py-2">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          <MixBars title="Property type" rows={profile.mix.propertyType} scopeLabel={profile.name} />
          <MixBars title="State" rows={profile.mix.state} scopeLabel={profile.name} />
          <MixBars title="Capital program" rows={profile.mix.capitalProgram} scopeLabel={profile.name} />
          <MixBars title="Deal size" rows={profile.mix.dealSizeBand} scopeLabel={profile.name} ordered limit={8} />
          <MixBars title="Purpose" rows={profile.mix.purpose} scopeLabel={profile.name} />
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-2 xl:grid-cols-4">
        <Panel
          title={internal ? 'My deals' : 'Deals'}
          kicker={<span className="flex items-center gap-1"><GitBranch size={10} aria-hidden="true" /> Split by stage, Triage first</span>}
          className="h-[460px] xl:col-span-3"
          bodyClassName="!p-0 flex min-h-0 flex-col"
        >
          <PersonDeals
            name={profile.name}
            holds={profile.deals}
            holdsLabel={holdsLabel}
            supportingIds={internal ? profile.supporting?.dealIds ?? [] : []}
            supportingBasis={profile.supporting?.basis}
          />
        </Panel>
        <Panel
          title="Recent activity"
          kicker={<span className="flex items-center gap-1"><Activity size={10} aria-hidden="true" /> Latest {profile.activity.length}</span>}
          className="h-[460px]"
          scroll
          footer="Each line is the event as recorded — field: old → new, or the event's own verb."
        >
          <ActivityTimeline items={profile.activity} />
        </Panel>
      </div>
    </div>
  )
}
